/**
 * TrainingRunsPanel · Phase B · Step 1 · ML Training Control Center.
 *
 * The admin's cockpit for kicking off custom YOLOv8 disc-detection
 * trainings on the harvested verified-pass corpus.
 *
 * Three render zones:
 *
 *   1. Header + ⚡ START button + epoch/model picker (form opens inline
 *      so the coach doesn't lose context).
 *   2. Active-run banner — shown only when a run is in `queued` or
 *      `training` status. Live progress bar, current epoch/loss/mAP,
 *      cancel button. Polls every 2s while present.
 *   3. History table — every past run, newest first, with status pill,
 *      mode badge, mAP scores, click-to-expand epoch chart.
 *
 * Wires to:
 *   POST   /api/admin/training-runs
 *   GET    /api/admin/training-runs
 *   GET    /api/admin/training-runs/{id}
 *   POST   /api/admin/training-runs/{id}/cancel
 *   DELETE /api/admin/training-runs/{id}
 *
 * Polling contract: while ANY run is queued/training we re-fetch the
 * full list every 2s. The poll stops automatically once the active set
 * empties. Manual ↻ button always available.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Brain, Loader2, Play, RefreshCw, Square, Trash2,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronUp,
  Sparkles, FlaskConical, Trophy, GitCompareArrows, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  listTrainingRuns,
  createTrainingRun,
  cancelTrainingRun,
  deleteTrainingRun,
  promoteTrainingRun,
  fehlerText,
} from "@/lib/api";
import RunCompareModal from "@/components/training/RunCompareModal";

const STATUS_STYLES = {
  queued:    { label: "Queued",    cls: "bg-blue-400/15 text-blue-300 border-blue-400/40" },
  training:  { label: "Training",  cls: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
  completed: { label: "Completed", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40" },
  failed:    { label: "Failed",    cls: "bg-rose-400/15 text-rose-300 border-rose-400/40" },
  cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground border-border" },
};

const POLL_INTERVAL_MS = 2000;
const MODEL_OPTIONS = ["yolov8n.pt", "yolov8s.pt", "yolov8m.pt"];

export default function TrainingRunsPanel() {
  const [runs, setRuns] = useState([]);
  const [bestCompleted, setBestCompleted] = useState(null);
  const [activeRunId, setActiveRunId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [promotingId, setPromotingId] = useState(null);
  const [form, setForm] = useState({
    mode: "simulated",
    model: "yolov8n.pt",
    epochs: 10,
  });
  const pollTimer = useRef(null);

  const hasActiveRun = useMemo(
    () => runs.some((r) => r.status === "queued" || r.status === "training"),
    [runs],
  );

  const completedCount = useMemo(
    () => runs.filter((r) => r.status === "completed" && (r.metrics_history || []).length > 0).length,
    [runs],
  );

  const load = useCallback(async () => {
    try {
      const data = await listTrainingRuns({ limit: 50 });
      setRuns(data.runs || []);
      setBestCompleted(data.best_completed || null);
      setActiveRunId(data.active_run_id || null);
    } catch (err) {
      const msg = fehlerText(err) || err?.message || "Failed to load training runs";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live polling — only while at least one run is active.
  useEffect(() => {
    if (!hasActiveRun) {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }
    if (pollTimer.current) return;
    pollTimer.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [hasActiveRun, load]);

  const onCreate = async () => {
    setCreating(true);
    try {
      const body = {
        mode: form.mode,
        config: {
          model: form.model,
          epochs: Number(form.epochs) || 10,
        },
      };
      const run = await createTrainingRun(body);
      toast.success(`Training run queued · ${run.config?.epochs || 10} epochs · ${run.mode}`);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(fehlerText(err) || "Failed to create training run");
    } finally {
      setCreating(false);
    }
  };

  const onCancel = async (id) => {
    if (!window.confirm("Cancel this active training run? Any partial epoch metrics are preserved.")) return;
    try {
      await cancelTrainingRun(id);
      toast.success("Training run cancelled");
      await load();
    } catch (err) {
      toast.error(fehlerText(err) || "Cancel failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Permanently delete this run record? On-disk weights stay; only the Mongo row is removed.")) return;
    try {
      await deleteTrainingRun(id);
      toast.success("Training run deleted");
      await load();
    } catch (err) {
      toast.error(fehlerText(err) || "Delete failed");
    }
  };

  const onPromote = async (run) => {
    if (run.id === activeRunId) {
      toast.info("This run is already the active inference model.");
      return;
    }
    const confirmMsg = `Promote run ${run.id.slice(0, 8)} (mAP50=${(run.best_mAP50 || 0).toFixed(3)}) ` +
                       "as the active inference model? All future tracking jobs will use its best.pt weights.";
    if (!window.confirm(confirmMsg)) return;
    setPromotingId(run.id);
    try {
      const res = await promoteTrainingRun(run.id);
      toast.success(`Active model swapped → ${res.weights_path}`);
      await load();
    } catch (err) {
      toast.error(fehlerText(err) || "Promote failed");
    } finally {
      setPromotingId(null);
    }
  };

  const runningRun = runs.find((r) => r.status === "queued" || r.status === "training");

  return (
    <div
      data-testid="training-runs-panel"
      className="border border-border bg-card rounded-sm overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/30 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-primary" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Phase B · Step 1 · Disc-Detection Custom YOLO
            </div>
            <div className="font-display font-black uppercase tracking-widest text-sm">
              ML Training Control Center
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="training-runs-compare-btn"
            size="sm" variant="ghost" onClick={() => setShowCompare(true)}
            disabled={completedCount < 2}
            className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30"
            title={completedCount < 2 ? "Need ≥2 completed runs with metrics to compare" : "Side-by-side run comparison"}
          >
            <GitCompareArrows className="w-3 h-3 mr-1" /> Compare
          </Button>
          <Button
            data-testid="training-runs-refresh"
            size="sm" variant="ghost" onClick={load} disabled={loading}
            className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest"
            title="Re-fetch the training-run list"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : (
              <><RefreshCw className="w-3 h-3 mr-1" /> Refresh</>
            )}
          </Button>
          <Button
            data-testid="training-runs-start-btn"
            size="sm"
            onClick={() => setShowForm((v) => !v)}
            disabled={hasActiveRun || creating}
            className="h-8 px-3 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-black uppercase tracking-widest text-[11px] disabled:opacity-40"
            title={hasActiveRun ? "An active run is already in flight" : "Open the launch panel"}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Start Custom Training Run
          </Button>
        </div>
      </div>

      {/* 🆕 v8c.8 (2026-02-28) · German operator-context banner explaining
          what's happening here. Surfaces only while a run is in flight
          so it's a contextual hint, not permanent visual noise. */}
      {hasActiveRun && (
        <div
          data-testid="training-runs-info-banner"
          className="px-4 py-3 border-b border-emerald-400/30 bg-emerald-400/[0.06] flex items-start gap-3"
        >
          <Brain className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <div className="text-[12px] leading-relaxed text-emerald-100">
            <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-300 mb-1">
              Info · Live YOLO-Training
            </div>
            <div>
              Die KI trainiert jetzt live ein maßgeschneidertes
              <span className="font-mono text-emerald-300"> YOLO-Modell </span>
              für die Scheiben-Erkennung auf diesem spezifischen Spielfeld.
              Dieser Vorgang läuft vollautomatisch im Hintergrund und dauert
              je nach Serverauslastung <strong>ca. 2–10 Minuten</strong>.
              Sie können dieses Fenster geöffnet lassen.
            </div>
          </div>
        </div>
      )}

      {/* Launch form */}
      {showForm && (
        <div
          data-testid="training-runs-form"
          className="grid sm:grid-cols-4 gap-3 px-4 py-3 border-b border-border bg-primary/[0.04]"
        >
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              Mode
            </span>
            <select
              data-testid="training-form-mode"
              value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value })}
              className="bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono"
            >
              <option value="simulated">Simulated (~30s · deterministic curve)</option>
              <option value="real">Real (ultralytics · needs ≥150 images)</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              Base Model
            </span>
            <select
              data-testid="training-form-model"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono"
            >
              {MODEL_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              Epochs
            </span>
            <input
              data-testid="training-form-epochs"
              type="number"
              min={1} max={300}
              value={form.epochs}
              onChange={(e) => setForm({ ...form, epochs: e.target.value })}
              className="bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono"
            />
          </label>
          <div className="flex items-end gap-2">
            <Button
              data-testid="training-form-submit"
              size="sm" onClick={onCreate} disabled={creating}
              className="h-8 px-3 bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400 hover:text-black border border-emerald-400/40 font-display font-bold uppercase tracking-widest text-[10px]"
            >
              {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Launch
            </Button>
            <Button
              size="sm" variant="ghost" onClick={() => setShowForm(false)} disabled={creating}
              className="h-8 px-3 font-mono text-[10px] uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Active run banner */}
      {runningRun && <ActiveRunBanner run={runningRun} onCancel={() => onCancel(runningRun.id)} />}

      {/* Best score callout */}
      {bestCompleted && (
        <div
          data-testid="training-best-score"
          className="px-4 py-2 border-b border-border bg-emerald-400/[0.03] flex items-center justify-between flex-wrap gap-2"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              Best run · mAP50 = {(bestCompleted.best_mAP50 || 0).toFixed(3)} · mAP50-95 = {(bestCompleted.best_mAP50_95 || 0).toFixed(3)}
            </span>
          </div>
          <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            {bestCompleted.config?.model || ""} · {bestCompleted.completed_at?.slice(0, 19).replace("T", " ")}
          </span>
        </div>
      )}

      {/* Active inference-model pointer */}
      {activeRunId && (
        <div
          data-testid="training-active-model-banner"
          className="px-4 py-2 border-b border-border bg-violet-400/[0.04] flex items-center justify-between flex-wrap gap-2"
        >
          <div className="flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5 text-violet-300" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-violet-300">
              Active inference model · run <span className="tabular-nums">{activeRunId.slice(0, 8)}</span>
            </span>
          </div>
          <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            tracking jobs use this run&apos;s best.pt
          </span>
        </div>
      )}

      {/* History table */}
      <div className="overflow-x-auto">
        {loading && runs.length === 0 ? (
          <div className="px-4 py-8 text-center text-muted-foreground text-xs">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
            Loading…
          </div>
        ) : runs.length === 0 ? (
          <div
            data-testid="training-runs-empty"
            className="px-4 py-8 text-center text-muted-foreground text-xs font-mono"
          >
            No training runs yet. Press <span className="text-primary">Start Custom Training Run</span> to kick off your first fit.
          </div>
        ) : (
          <table className="w-full font-mono text-[11px]">
            <thead className="bg-secondary/20 border-b border-border">
              <tr className="text-left">
                <Th>When</Th>
                <Th>Status</Th>
                <Th>Mode</Th>
                <Th>Model</Th>
                <Th>Epochs</Th>
                <Th right>mAP50</Th>
                <Th right>mAP50-95</Th>
                <Th right>Loss</Th>
                <Th />
              </tr>
            </thead>
            <tbody data-testid="training-runs-table" className="divide-y divide-border">
              {runs.map((r) => (
                <RunRow
                  key={r.id} run={r}
                  expanded={expandedId === r.id}
                  isActive={r.id === activeRunId}
                  promoting={promotingId === r.id}
                  onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  onDelete={() => onDelete(r.id)}
                  onCancel={() => onCancel(r.id)}
                  onPromote={() => onPromote(r)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCompare && (
        <RunCompareModal runs={runs} onClose={() => setShowCompare(false)} />
      )}
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th className={`px-3 py-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground ${right ? "text-right" : ""}`}>
      {children}
    </th>
  );
}

function Td({ children, right, className = "" }) {
  return (
    <td className={`px-3 py-2 tabular-nums ${right ? "text-right" : ""} ${className}`}>
      {children}
    </td>
  );
}

function RunRow({ run, expanded, isActive, promoting, onToggle, onDelete, onCancel, onPromote }) {
  const s = STATUS_STYLES[run.status] || STATUS_STYLES.queued;
  const isRunning = run.status === "queued" || run.status === "training";
  const created = run.created_at?.slice(11, 19);
  const date = run.created_at?.slice(5, 10);
  const isPromotable = run.status === "completed" && (run.best_mAP50 || 0) > 0;

  return (
    <>
      <tr
        data-testid={`training-run-row-${run.id}`}
        className={`hover:bg-accent/20 transition-colors ${expanded ? "bg-accent/10" : ""} ${isActive ? "bg-violet-400/[0.04]" : ""}`}
      >
        <Td className="whitespace-nowrap">
          <button type="button" onClick={onToggle} className="flex items-center gap-1.5 hover:text-primary">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span className="text-muted-foreground">{date}</span> {created}
            {isActive && (
              <Badge
                data-testid={`training-run-active-badge-${run.id}`}
                className="ml-1.5 bg-violet-400/15 text-violet-200 border-violet-400/40 border font-mono text-[8px] uppercase tracking-widest gap-1"
              >
                <Crown className="w-2.5 h-2.5" /> Active Model
              </Badge>
            )}
          </button>
        </Td>
        <Td>
          <Badge className={`${s.cls} border font-mono text-[9px] uppercase tracking-widest`}>
            {isRunning && <Loader2 className="w-2.5 h-2.5 animate-spin inline mr-1" />}
            {s.label}
          </Badge>
        </Td>
        <Td>
          {/* 🆕 11.09.2026 · Das Abzeichen zeigt, was WIRKLICH gelaufen
              ist. `mode` ist nur der gewuenschte Modus — stand dort
              "real" und Modal war nicht erreichbar, lief trotzdem der
              eingebaute Simulator, und die Zeile behauptete REAL. */}
          {(() => {
            const echt = run.mode_effective || run.mode;
            const istEcht = echt === "real";
            const abweichung = run.mode_effective
              && run.mode_effective !== run.mode;
            return (
              <Badge
                title={abweichung
                  ? `angefordert: ${run.mode} · tatsächlich: ${echt}`
                  : undefined}
                className={
                  istEcht
                    ? "bg-violet-400/15 text-violet-300 border-violet-400/40 border font-mono text-[9px] uppercase tracking-widest"
                    : "bg-muted text-muted-foreground border-border border font-mono text-[9px] uppercase tracking-widest"
                }>
                {istEcht ? <FlaskConical className="w-2.5 h-2.5 inline mr-1" /> : null}
                {echt}{abweichung ? "*" : ""}
              </Badge>
            );
          })()}
        </Td>
        <Td className="text-foreground/80">{run.config?.model || "—"}</Td>
        <Td>{run.epochs_done || 0} / {run.config?.epochs || 0}</Td>
        <Td right className="text-emerald-300">{run.best_mAP50 != null ? run.best_mAP50.toFixed(3) : "—"}</Td>
        <Td right className="text-emerald-300/80">{run.best_mAP50_95 != null ? run.best_mAP50_95.toFixed(3) : "—"}</Td>
        <Td right className="text-amber-300/80">{run.current_loss != null ? run.current_loss.toFixed(3) : "—"}</Td>
        <Td right>
          <div className="flex items-center gap-1 justify-end">
            {isPromotable && !isActive && (
              <Button
                data-testid={`training-run-promote-${run.id}`}
                size="sm" variant="ghost" onClick={onPromote}
                disabled={promoting}
                className="h-6 px-1.5 bg-violet-400/10 hover:bg-violet-400/30 text-violet-300 border border-violet-400/30 font-display font-bold uppercase tracking-widest text-[9px]"
                title="Promote this run's best.pt as the active inference model"
              >
                {promoting ? <Loader2 className="w-2.5 h-2.5 animate-spin mr-0.5" /> : <Trophy className="w-2.5 h-2.5 mr-0.5" />}
                Promote
              </Button>
            )}
            {isRunning ? (
              <Button
                data-testid={`training-run-cancel-${run.id}`}
                size="sm" variant="ghost" onClick={onCancel}
                className="h-6 w-6 p-0 hover:bg-rose-400/20 hover:text-rose-300"
                title="Cancel this run"
              >
                <Square className="w-3 h-3" />
              </Button>
            ) : (
              <Button
                data-testid={`training-run-delete-${run.id}`}
                size="sm" variant="ghost" onClick={onDelete}
                className="h-6 w-6 p-0 hover:bg-destructive/20 hover:text-destructive"
                title="Delete this run record"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        </Td>
      </tr>
      {expanded && (
        <tr data-testid={`training-run-expanded-${run.id}`}>
          <td colSpan={9} className="bg-secondary/10 border-y border-border">
            <RunDetails run={run} />
          </td>
        </tr>
      )}
    </>
  );
}

function RunDetails({ run }) {
  const ds = run.dataset_stats;
  const metrics = run.metrics_history || [];
  const isModalRun = !!run.modal_job_id;

  return (
    <div className="px-4 py-3 grid md:grid-cols-3 gap-4">
      <div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-2">
          Dataset
        </div>
        {ds ? (
          <ul className="font-mono text-[11px] space-y-0.5">
            <li>source samples: <span className="text-primary tabular-nums">{ds.source_samples}</span></li>
            <li>images / labels: <span className="text-primary tabular-nums">{ds.images_total} / {ds.labels_total}</span></li>
            <li>train / val / test: <span className="text-primary tabular-nums">{ds.train_images} / {ds.val_images} / {ds.test_images}</span></li>
            <li>classes: {(ds.classes || []).join(", ")}</li>
          </ul>
        ) : (
          <div className="font-mono text-[11px] text-muted-foreground/60 italic">
            export not yet complete
          </div>
        )}
        {ds?.notes && (
          <div className="mt-2 flex items-start gap-1.5 text-amber-300/80 font-mono text-[10px]">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{ds.notes}</span>
          </div>
        )}
        {/* Phase B · Step 3 — Modal job link & mocked-mode banner */}
        {isModalRun && (
          <div
            data-testid={`training-modal-info-${run.id}`}
            className="mt-2 text-[10px] font-mono space-y-0.5"
          >
            <div className="text-violet-300/80">
              Modal job: <span className="tabular-nums">{run.modal_job_id}</span>
            </div>
            {run.modal_status_url ? (
              <a
                href={run.modal_status_url}
                target="_blank" rel="noopener noreferrer"
                className="text-primary underline decoration-dotted underline-offset-2"
              >
                ↗ Open on Modal
              </a>
            ) : (
              <div className="text-amber-300/80">
                MOCKED Modal · set MODAL_APP_URL + MODAL_TOKEN_ID + MODAL_TOKEN_SECRET + MODAL_WEBHOOK_SECRET in backend/.env to enable real GPU offload
              </div>
            )}
          </div>
        )}
        {run.error && (
          <div className="mt-2 flex items-start gap-1.5 text-rose-300 font-mono text-[10px]">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{run.error}</span>
          </div>
        )}
      </div>

      <div className="md:col-span-2">
        <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-2">
          Epoch metrics ({metrics.length} of {run.config?.epochs || 0})
        </div>
        {metrics.length === 0 ? (
          <div className="font-mono text-[11px] text-muted-foreground/60 italic">
            waiting for first epoch…
          </div>
        ) : (
          <>
            <MetricsChart metrics={metrics} />
            <div className="mt-2 max-h-32 overflow-y-auto">
              <table className="w-full font-mono text-[10px]">
                <thead className="text-muted-foreground sticky top-0 bg-card">
                  <tr className="text-left">
                    <th className="py-1 pr-2">epoch</th>
                    <th className="py-1 pr-2 text-right">loss</th>
                    <th className="py-1 pr-2 text-right">mAP50</th>
                    <th className="py-1 pr-2 text-right">mAP50-95</th>
                    <th className="py-1 pr-2 text-right">elapsed</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((m, i) => (
                    <tr key={`${m.epoch}-${i}`} className="tabular-nums">
                      <td className="py-0.5 pr-2 text-primary">{m.epoch}</td>
                      <td className="py-0.5 pr-2 text-right text-amber-300/80">{m.loss?.toFixed(3)}</td>
                      <td className="py-0.5 pr-2 text-right text-emerald-300">{m.mAP50?.toFixed(3)}</td>
                      <td className="py-0.5 pr-2 text-right text-emerald-300/80">{m.mAP50_95?.toFixed(3)}</td>
                      <td className="py-0.5 pr-2 text-right text-muted-foreground">{m.elapsed_s?.toFixed(1)}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Tiny inline SVG line chart for the epoch metrics curve. */
function MetricsChart({ metrics }) {
  if (!metrics.length) return null;
  const W = 320, H = 80, P = 8;
  const xs = metrics.map((_, i) => P + (i / Math.max(1, metrics.length - 1)) * (W - 2 * P));
  const pathFor = (key, max = 1.0) => metrics.map((m, i) => {
    const v = (m[key] || 0) / max;
    return `${i === 0 ? "M" : "L"} ${xs[i].toFixed(1)} ${(H - P - v * (H - 2 * P)).toFixed(1)}`;
  }).join(" ");
  const maxLoss = Math.max(...metrics.map((m) => m.loss || 0), 1.0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" preserveAspectRatio="none">
      {/* grid line at y=0.5 */}
      <line x1={P} x2={W - P} y1={H / 2} y2={H / 2} stroke="hsl(var(--border))" strokeWidth={0.5} strokeDasharray="2 2" />
      {/* loss (amber, normalised by max) */}
      <path d={pathFor("loss", maxLoss)} stroke="#FCD34D" strokeWidth={1.2} fill="none" opacity={0.7} />
      {/* mAP50 (emerald) */}
      <path d={pathFor("mAP50", 1.0)} stroke="#6EE7B7" strokeWidth={1.5} fill="none" />
      {/* mAP50-95 (emerald-dim) */}
      <path d={pathFor("mAP50_95", 1.0)} stroke="#6EE7B7" strokeWidth={1.2} fill="none" opacity={0.55} strokeDasharray="3 2" />
    </svg>
  );
}

function ActiveRunBanner({ run, onCancel }) {
  const epochs = run.config?.epochs || 0;
  const done = run.epochs_done || 0;
  const pct = epochs > 0 ? Math.min(100, (done / epochs) * 100) : 0;
  return (
    <div
      data-testid="training-active-run-banner"
      className="px-4 py-3 border-b border-border bg-amber-400/[0.04]"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 text-amber-300 animate-spin" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-amber-200">
            Active run · {run.mode}
          </span>
          <span className="font-mono text-[10px] tabular-nums text-foreground/80">
            epoch <span className="text-primary font-bold">{done}</span> / {epochs}
            {run.current_loss != null && (
              <span className="ml-3">loss = <span className="text-amber-300">{run.current_loss.toFixed(3)}</span></span>
            )}
            {run.best_mAP50 != null && (
              <span className="ml-3">mAP50 = <span className="text-emerald-300">{run.best_mAP50.toFixed(3)}</span></span>
            )}
          </span>
        </div>
        <Button
          data-testid="training-active-cancel-btn"
          size="sm" variant="ghost" onClick={onCancel}
          className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-rose-400/20 hover:text-rose-300"
        >
          <Square className="w-3 h-3 mr-1" /> Cancel
        </Button>
      </div>
      <div className="mt-2 h-1.5 bg-background rounded-full overflow-hidden">
        <div
          data-testid="training-active-progress-bar"
          className="h-full bg-amber-300 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
